import { LoadingSpinner } from "@/components/common/LoadingSpinner/LoadingSpinner"
import { createEmptyChat } from "@/components/feature/Chat/utils/chatUtils"
import { ConversationActions } from "@/components/feature/ChatHistory/ConversationActions"
import { Button } from "@/components/ui/button"
import { useAgentChatContext } from "@/contexts/AgentChatContext"
import { useChatContext } from "@/contexts/ChatContext"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { useGetChatConversations } from "@/services/api/chatConversationService"
import { PlusIcon } from "lucide-react"

interface ChatThreadListProps {
  onSelectConversation?: () => void
}

export function ChatThreadList({ onSelectConversation }: ChatThreadListProps) {
  const { translate } = useTranslation()
  const { chat, setChat } = useChatContext()
  const agentChat = useAgentChatContext()
  const { data, isPending, isError } = useGetChatConversations(1, 50)

  const handleSelectConversation = (conversationId: string) => {
    if (conversationId !== chat.id) {
      setChat({ ...createEmptyChat(), id: conversationId })
      agentChat.resetForNewConversation()
    }
    onSelectConversation?.()
  }

  const handleNewChat = () => {
    setChat(createEmptyChat())
    agentChat.resetForNewConversation()
    onSelectConversation?.()
  }

  const conversations = data?.conversations ?? []

  return (
    <div className="flex h-full w-full flex-col">
      <div className="flex items-center justify-end border-b border-border px-3 py-2.5">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 gap-1.5 px-2"
          onClick={handleNewChat}
        >
          <PlusIcon className="h-4 w-4" />
          {translate("AI_CHAT/NEW_CONVERSATION")}
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto p-2">
        {isPending ? (
          <div className="flex justify-center py-6">
            <LoadingSpinner size={20} />
          </div>
        ) : isError ? (
          <p className="px-2 py-1 text-xs text-muted-foreground">
            {translate("AI_CHAT/HISTORY/LIST_ERROR")}
          </p>
        ) : conversations.length === 0 ? (
          <p className="px-2 py-1 text-xs text-muted-foreground">
            {translate("AI_CHAT/HISTORY/EMPTY")}
          </p>
        ) : (
          <ul className="space-y-0.5">
            {conversations.map((conversation) => {
              const isActive = conversation.id === chat.id

              return (
                <li
                  key={conversation.id}
                  className={cn(
                    "group/item flex items-center gap-1 rounded-md",
                    isActive
                      ? "bg-sidebar-accent text-sidebar-accent-foreground"
                      : "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                  )}
                >
                  <button
                    type="button"
                    className="min-w-0 flex-1 truncate px-2 py-1.5 text-left text-sm"
                    title={conversation.title}
                    onClick={() => handleSelectConversation(conversation.id)}
                  >
                    {conversation.title}
                  </button>
                  <ConversationActions
                    compact
                    showOnHover
                    conversationId={conversation.id}
                    title={conversation.title}
                  />
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}
